import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShoppingCart,
  Truck,
  HeartPulse,
  Store,
  ArrowRight,
} from "lucide-react";

import {
  RevealSection,
  SplitText,
  MagneticButton,
  staggerContainer,
  staggerItem,
  useParallax,
  EASE,
} from "../components/motion/ScrollFX";

// Same theme tokens as Work.jsx / Careers.jsx so all pages read as one product.
const theme = {
  ink: "#221619",
  inkMuted: "#6B6265",
  accent: "#E63946",
  accentDeep: "#B22234",
  gold: "#D9A441",
  border: "#E6DFDC",
  surface: "#FBF8F6",
  surfaceAlt: "#F2ECE9",
  heroBg: "#1A0E10",
  darkBand: "#221619",
  darkBandDeep: "#160B0D",
  ctaBg: "#2B1013",
  ctaBgDeep: "#160A0C",
};

const solutions = [
  {
    title: "E-Commerce",
    description:
      "Empower online businesses with secure payments, seller onboarding, and customer verification.",
    icon: ShoppingCart,
    path: "/solutions/ecommerce",
  },
  {
    title: "Logistics",
    description:
      "Simplify fleet operations with driver verification, COD settlements, and instant payouts.",
    icon: Truck,
    path: "/solutions/logistics",
  },
  {
    title: "Healthcare",
    description:
      "Digitize healthcare with secure payments, patient verification, and claims processing.",
    icon: HeartPulse,
    path: "/solutions/healthcare",
  },
  {
    title: "Marketplace",
    description:
      "Manage sellers, automate KYC, handle commissions, and streamline vendor payouts.",
    icon: Store,
    path: "/solutions/marketplace",
  },
];

const industryItems = ["E-Commerce", "Logistics", "Healthcare", "Marketplace", "Fintech", "SaaS"];

function Eyebrow({ children, center = false, light = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase mb-4 ${
        center ? "mx-auto justify-center" : ""
      }`}
      style={{ color: light ? "#FF8A93" : theme.accent }}
    >
      <span className="h-px w-6" style={{ backgroundColor: light ? "#FF8A93" : theme.accent }} />
      {children}
    </div>
  );
}

function DotGrid({ className = "", light = false }) {
  return (
    <div
      className={`pointer-events-none absolute inset-0 ${light ? "opacity-[0.05]" : "opacity-[0.06]"} ${className}`}
      style={{
        backgroundImage: `radial-gradient(${light ? "#FFFFFF" : theme.ink} 1px, transparent 1px)`,
        backgroundSize: "26px 26px",
      }}
    />
  );
}

function IndustryBand() {
  return (
    <div
      className="relative py-8 overflow-hidden border-y"
      style={{ backgroundColor: theme.darkBandDeep, borderColor: "rgba(255,255,255,0.08)" }}
    >
      <style>{`
        @keyframes solutionsMarquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .solutions-marquee-track { animation: solutionsMarquee 22s linear infinite; }
        @media (prefers-reduced-motion: reduce) { .solutions-marquee-track { animation: none; } }
      `}</style>
      <div className="flex w-max solutions-marquee-track">
        {[...industryItems, ...industryItems].map((item, i) => (
          <span key={i} className="px-10 text-lg font-bold whitespace-nowrap" style={{ color: "rgba(255,255,255,0.35)" }}>
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// NEW hero background — "modular blueprint" motif, distinct from both
// Work's aurora/beam field and Careers' rising-particle growth field.
// Floating rounded tiles drift/rotate independently; faint connector
// lines between nearby tiles fade in and out like a diagram assembling
// itself; a slow horizontal scan line sweeps top to bottom.
// ---------------------------------------------------------------------
const blueprintTiles = [
  { x: 10, y: 20, size: 46, delay: 0 },
  { x: 28, y: 12, size: 30, delay: 0.6 },
  { x: 46, y: 26, size: 54, delay: 1.1 },
  { x: 20, y: 46, size: 34, delay: 1.6 },
  { x: 40, y: 58, size: 42, delay: 0.3 },
  { x: 62, y: 40, size: 30, delay: 2.0 },
  { x: 78, y: 22, size: 46, delay: 0.9 },
  { x: 70, y: 64, size: 36, delay: 1.4 },
  { x: 88, y: 52, size: 26, delay: 2.4 },
  { x: 8, y: 70, size: 28, delay: 1.8 },
];
const blueprintLinks = [
  [0, 1], [1, 2], [1, 3], [2, 5], [2, 6], [3, 4], [4, 5], [5, 6], [6, 8], [5, 7], [7, 8], [3, 9],
];

function BlueprintField() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <span className="blueprint-glow" />
      <span className="blueprint-scanline" />
      <DotGrid light />

      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        {blueprintLinks.map(([a, b], i) => {
          const t1 = blueprintTiles[a];
          const t2 = blueprintTiles[b];
          return (
            <line
              key={i}
              x1={t1.x} y1={t1.y} x2={t2.x} y2={t2.y}
              stroke={i % 3 === 0 ? theme.gold : theme.accent}
              strokeWidth="0.12"
              strokeDasharray="1.2 2.4"
              className="blueprint-link"
              style={{ animationDelay: `${i * 0.4}s` }}
            />
          );
        })}
      </svg>

      {blueprintTiles.map((t, i) => (
        <span
          key={i}
          className="blueprint-tile"
          style={{
            left: `${t.x}%`,
            top: `${t.y}%`,
            width: t.size,
            height: t.size,
            animationDelay: `${t.delay}s`,
            borderColor: i % 3 === 0 ? "rgba(217,164,65,0.35)" : "rgba(255,255,255,0.16)",
          }}
        >
          <span className="blueprint-tile-dot" style={{ backgroundColor: i % 3 === 0 ? theme.gold : theme.accent }} />
        </span>
      ))}
    </div>
  );
}

export default function Solutions() {
  const heroRef = useRef(null);
  const bgParallax = useParallax(heroRef, 40);

  return (
    <div className="w-full" style={{ backgroundColor: theme.surface }}>
      <style>{`
        .blueprint-glow {
          position: absolute; top: 30%; left: 50%; width: 50rem; height: 50rem;
          margin: -25rem 0 0 -25rem; border-radius: 9999px;
          background: radial-gradient(circle, ${theme.accent}30, transparent 68%);
          filter: blur(70px);
          animation: blueprintGlowPulse 8s ease-in-out infinite;
        }
        @keyframes blueprintGlowPulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.15); }
        }

        .blueprint-scanline {
          position: absolute; left: 0; right: 0; height: 1px; top: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent);
          box-shadow: 0 0 12px 2px rgba(255,255,255,0.25);
          animation: blueprintScan 7s ease-in-out infinite;
        }
        @keyframes blueprintScan {
          0% { top: 0%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }

        .blueprint-link {
          animation: blueprintLinkFade 5s ease-in-out infinite;
        }
        @keyframes blueprintLinkFade {
          0%, 100% { opacity: 0; stroke-dashoffset: 12; }
          40% { opacity: 0.55; }
          60% { opacity: 0.55; }
          100% { opacity: 0; stroke-dashoffset: 0; }
        }

        .blueprint-tile {
          position: absolute; border-radius: 14px; border: 1px solid;
          background: rgba(255,255,255,0.03);
          transform: translate(-50%, -50%);
          animation: blueprintTileFloat 9s ease-in-out infinite;
          backdrop-filter: blur(2px);
        }
        @keyframes blueprintTileFloat {
          0%, 100% { transform: translate(-50%, -50%) rotate(0deg); }
          50% { transform: translate(-50%, calc(-50% - 10px)) rotate(4deg); }
        }
        .blueprint-tile-dot {
          position: absolute; top: 8px; left: 8px; width: 6px; height: 6px;
          border-radius: 9999px; opacity: 0.9;
        }

        @media (prefers-reduced-motion: reduce) {
          .blueprint-glow, .blueprint-scanline, .blueprint-link, .blueprint-tile {
            animation: none !important;
          }
        }

        .card-glow {
          pointer-events: none; position: absolute; top: -3rem; right: -3rem; width: 9rem; height: 9rem;
          border-radius: 9999px; filter: blur(30px); background: ${theme.accent}; opacity: 0.08; transition: opacity 0.3s ease;
        }
        .group:hover .card-glow { opacity: 0.16; }
      `}</style>

      {/* ================= HERO (dark, blueprint field bg) ================= */}
      <section ref={heroRef} className="relative overflow-hidden pt-40 pb-28 md:pt-48 md:pb-32" style={{ backgroundColor: theme.heroBg }}>
        <motion.div style={{ y: bgParallax }} className="absolute inset-0">
          <BlueprintField />
        </motion.div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <Eyebrow center light>Industry Solutions</Eyebrow>
            <h1 className="text-4xl md:text-[3.4rem] font-extrabold leading-[1.08] tracking-tight text-white">
              <SplitText text="Solutions built for" className="text-white" />
              <br />
              <span style={{ color: theme.accent }}>
                <SplitText text="every industry" delay={0.25} />
              </span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55, ease: EASE }}
              className="mt-6 text-lg text-white/60 max-w-xl mx-auto"
            >
              Discover scalable technology solutions designed to accelerate growth across modern businesses.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7, ease: EASE }}
              className="mt-10 flex flex-wrap items-center justify-center gap-4"
            >
              <MagneticButton>
                <Link
                  to="/contact"
                  data-cursor="hover"
                  style={{ backgroundColor: theme.accent }}
                  className="group inline-flex items-center gap-2 text-white px-7 py-3.5 rounded-md font-semibold hover:brightness-110 transition-all"
                >
                  Talk to Us
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </MagneticButton>
              <MagneticButton strength={0.25}>
                
                <a  href="#solutions-grid"
                  data-cursor="hover"
                  className="inline-flex items-center gap-2 border border-white/25 text-white px-7 py-3.5 rounded-md font-medium hover:bg-white hover:text-[#221619] transition-colors"
                >
                  Explore Solutions
                </a>
              </MagneticButton>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <IndustryBand />

      {/* ================= SOLUTIONS GRID (light) ================= */}
      <section id="solutions-grid" className="relative py-24" style={{ backgroundColor: theme.surface }}>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <RevealSection className="text-center max-w-2xl mx-auto mb-16">
            <Eyebrow center>What We Solve</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold" style={{ color: theme.ink }}>
              <SplitText text="Built around your" /> <span style={{ color: theme.accent }}>industry</span>
            </h2>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid md:grid-cols-2 gap-6"
          >
            {solutions.map((solution) => {
              const Icon = solution.icon;
              return (
                <motion.div key={solution.title} variants={staggerItem}>
                  <Link
                    to={solution.path}
                    data-cursor="hover"
                    className="group relative block p-8 rounded-2xl border overflow-hidden hover:-translate-y-2 transition-all duration-300"
                    style={{ backgroundColor: theme.surface, borderColor: theme.border }}
                  >
                    <span className="card-glow" />
                    <div
                      className="relative w-16 h-16 flex items-center justify-center rounded-xl mb-6"
                      style={{ backgroundColor: `${theme.accent}1A`, color: theme.accent }}
                    >
                      <Icon size={30} />
                    </div>

                    <h2 className="relative text-2xl font-bold" style={{ color: theme.ink }}>
                      {solution.title}
                    </h2>

                    <p className="relative mt-4 leading-7" style={{ color: theme.inkMuted }}>
                      {solution.description}
                    </p>

                    <div
                      className="relative flex items-center gap-2 mt-8 font-semibold group-hover:gap-4 transition-all"
                      style={{ color: theme.accent }}
                    >
                      Explore Solution
                      <ArrowRight size={18} />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ================= CTA (dark) ================= */}
      <section
        className="relative py-28 overflow-hidden"
        style={{ background: `linear-gradient(160deg, ${theme.ctaBg} 0%, ${theme.ctaBgDeep} 100%)` }}
      >
        <DotGrid light />
        <div
          className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full blur-3xl opacity-[0.14]"
          style={{ backgroundColor: theme.accent }}
        />

        <RevealSection className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <Eyebrow center light>Let's Talk</Eyebrow>
          <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-white">
            <SplitText text="Have an industry" />
            <span className="block">
              <SplitText text="in mind?" delay={0.15} />
            </span>
          </h2>
          <p className="mt-6 text-lg text-white/60 max-w-xl mx-auto">
            Tell us what you're building — we'll show you how our platform fits your industry.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <MagneticButton>
              <Link
                to="/contact"
                data-cursor="hover"
                style={{ backgroundColor: theme.accent }}
                className="group inline-flex items-center gap-2 px-9 py-4 rounded-md text-white font-semibold text-lg shadow-lg shadow-black/30 hover:brightness-110 transition-all"
              >
                Start a Project
                <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </MagneticButton>
          </div>
        </RevealSection>
      </section>
    </div>
  );
}